import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { InlineWidget } from 'react-calendly';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ isOpen, onClose, initialTab = 'pitch' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Update activeTab when initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setSubmitStatus(null);
    }
  }, [isOpen, initialTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact`, {
        name: formData.name,
        company: formData.company,
        email: formData.email
      });

      if (response.data.status === 'success') {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '' });
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-[#111111] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#2A2A2A] shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-[#111111] border-b border-[#2A2A2A] p-6 flex items-center justify-between">
                <h3
                  className="text-white text-2xl font-bold"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                >
                  İletişim
                </h3>
                <button
                  onClick={onClose}
                  className="text-[#F0EBE3]/60 hover:text-white transition-colors duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-[#2A2A2A]">
                <button
                  onClick={() => setActiveTab('pitch')}
                  className={`flex-1 py-4 text-sm font-medium transition-all duration-300 ${
                    activeTab === 'pitch'
                      ? 'text-[#C8102E] border-b-2 border-[#C8102E]'
                      : 'text-[#F0EBE3]/60 hover:text-[#F0EBE3]'
                  }`}
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                >
                  Pitch Deck İste
                </button>
                <button
                  onClick={() => setActiveTab('meeting')}
                  className={`flex-1 py-4 text-sm font-medium transition-all duration-300 ${
                    activeTab === 'meeting'
                      ? 'text-[#C8102E] border-b-2 border-[#C8102E]'
                      : 'text-[#F0EBE3]/60 hover:text-[#F0EBE3]'
                  }`}
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                >
                  Görüşme Ayarla
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {activeTab === 'pitch' ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-[#F0EBE3] text-sm mb-2"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      >
                        Ad Soyad
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-lg px-4 py-3 focus:outline-none focus:border-[#C8102E] transition-colors duration-300"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="company"
                        className="block text-[#F0EBE3] text-sm mb-2"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      >
                        Şirket / Fon Adı
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-lg px-4 py-3 focus:outline-none focus:border-[#C8102E] transition-colors duration-300"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-[#F0EBE3] text-sm mb-2"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      >
                        E-posta
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#1A1A1A] text-white border border-[#2A2A2A] rounded-lg px-4 py-3 focus:outline-none focus:border-[#C8102E] transition-colors duration-300"
                        style={{ fontFamily: "'Ubuntu', sans-serif" }}
                      />
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting || submitStatus === 'success'}
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      className={`w-full py-4 rounded-full font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-2 ${
                        submitStatus === 'success' 
                          ? 'bg-green-600 text-white' 
                          : submitStatus === 'error'
                          ? 'bg-red-600 text-white'
                          : 'bg-[#C8102E] text-white hover:bg-[#A00D25]'
                      } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : submitStatus === 'success' ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Talebiniz Alındı!
                        </>
                      ) : submitStatus === 'error' ? (
                        <>
                          <AlertCircle className="w-5 h-5" />
                          Hata! Tekrar Deneyin
                        </>
                      ) : (
                        'Gönder'
                      )}
                    </motion.button>

                    <p
                      className="text-[#F0EBE3]/50 text-xs text-center"
                      style={{ fontFamily: "'Ubuntu', sans-serif" }}
                    >
                      {submitStatus === 'success' 
                        ? 'E-posta adresinize onay mesajı gönderildi.'
                        : 'Bilgileriniz yalnızca iletişim amacıyla kullanılır.'}
                    </p>
                  </form>
                ) : (
                  <div className="space-y-5">
                    <p
                      className="text-[#F0EBE3] text-sm mb-6"
                      style={{ fontFamily: "'Ubuntu', sans-serif" }}
                    >
                      Görüşme için aşağıdaki takvimden uygun bir slot seçin:
                    </p>

                    {/* Calendly Inline Widget */}
                    <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-[#2A2A2A]">
                      <InlineWidget 
                        url="https://calendly.com/anilunkaya"
                        styles={{
                          height: '630px',
                          minWidth: '100%'
                        }}
                      />
                    </div>

                    <p
                      className="text-[#F0EBE3]/50 text-xs text-center"
                      style={{ fontFamily: "'Ubuntu', sans-serif" }}
                    >
                      48 saat içinde size geri dönüş yapacağız.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
